import React from 'react';
import { Container, Typography, Box, Card, Grid, useTheme, Divider, Chip } from '@mui/material';
import { LocalShipping, AccessTime, Support, CheckCircle, Autorenew, MarkEmailRead } from '@mui/icons-material';

const ShippingDelivery = () => {
  const theme = useTheme();

  const deliveryFeatures = [
    {
      icon: <AccessTime sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Instant Access",
      description: "Upon successful payment, your account is updated automatically and you get immediate access to all premium features."
    },
    {
      icon: <CheckCircle sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "100% Digital Services",
      description: "All memberships and premium features are delivered online through your account dashboard. No physical shipping, no delays."
    },
    {
      icon: <MarkEmailRead sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Confirmation Email",
      description: "After every purchase, a confirmation email is sent to your registered email address as proof of purchase and with all relevant details."
    },
    {
      icon: <Autorenew sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Subscription Renewals",
      description: "Auto-renewal subscriptions are processed automatically. You will be notified by email before renewal. All services remain digital."
    }
  ];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ 
        py: 10,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box
          component="img"
          src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
          alt="Digital Delivery Background"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.08,
            top: 0,
            left: 0
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <LocalShipping sx={{ fontSize: 80, color: 'white' }} />
            </Box>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Shipping & Delivery
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                opacity: 0.9,
                mb: 4
              }}
            >
              All services at seethakalyanavaibhogame.com are delivered instantly and digitally. No physical shipping, no waiting—your journey starts the moment you subscribe!
            </Typography>
            <Chip 
              label="Digital Services Only"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 600,
                px: 3,
                py: 1
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Delivery Features */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            How Our Digital Delivery Works
          </Typography>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {deliveryFeatures.map((feature, index) => (
              <Grid item xs={12} md={3} key={index} sx={{ minWidth: 300, maxWidth: 300 }}>
                <Card sx={{ 
                  p: 4,
                  textAlign: 'center',
                  height: '260px',
                  transition: 'all 0.3s ease',
                  borderRadius: '16px',
                  border: `2px solid ${theme.palette.primary.main}`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ mb: 3 }}>
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Information */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="md">
          <Card sx={{ 
            p: 6,
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
            mb: 6
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 3
              }}
            >
              Why Digital Delivery?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                lineHeight: 1.8,
                opacity: 0.9,
                maxWidth: '700px',
                mx: 'auto',
                mb: 3
              }}
            >
              At seethakalyanavaibhogame.com, we do not deliver physical products. All services—including memberships and premium features—are provided entirely online. Once payment is confirmed, your account is updated automatically and you have immediate access to the features you subscribed to.
            </Typography>
            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.3)' }} />
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                mb: 2
              }}
            >
              <b>Confirmation Email:</b> After purchasing any premium service or subscription, a confirmation email will be sent to your registered email address. This email serves as proof of purchase and includes details of your subscription.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                mb: 2
              }}
            >
              <b>Subscription Renewals:</b> If you have opted for auto-renewal, the renewal will occur automatically at the end of your subscription period. You will be notified by email before the renewal. All services remain digital—no shipping or delivery required.
            </Typography>
          </Card>
        </Container>
      </Box>

      {/* Support Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Need Help?
          </Typography>
          <Card sx={{ 
            p: 6,
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            textAlign: 'center',
            mb: 6
          }}>
            <Support sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 3 }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2
              }}
            >
              Customer Support
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                mb: 3
              }}
            >
              If you have any questions about our digital delivery or need assistance, please contact us:
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
                mb: 1
              }}
            >
              info@seethakalyanavaibhogame.com 
            </Typography>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default ShippingDelivery; 